import type { TrendBucket } from '../../lib/aggregates.ts';

interface TrendByMonthProps {
  buckets: readonly TrendBucket[];
}

const BAR_W = 38;
const BAR_GAP = 8;
const BAR_MAX_H = 110;
const Y_AXIS_W = 28;
const X_AXIS_H = 28;
const TOP_PAD = 12;

export function TrendByMonth({ buckets }: TrendByMonthProps) {
  const totalJobs = buckets.reduce((sum, b) => sum + b.jobCount, 0);
  const width = Y_AXIS_W + buckets.length * (BAR_W + BAR_GAP);
  const height = TOP_PAD + BAR_MAX_H + X_AXIS_H;

  return (
    <div style={cardStyle}>
      <div style={labelStyle}>TREND BY MONTH</div>
      {totalJobs === 0 ? (
        <div style={{ color: '#525870', fontSize: 13, marginTop: 12 }}>
          No promotions in the last {buckets.length} months.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <svg
            width={width}
            height={height}
            role="img"
            aria-label="Mean grade per month over the last twelve months"
          >
            <line
              x1={Y_AXIS_W}
              y1={TOP_PAD}
              x2={Y_AXIS_W}
              y2={TOP_PAD + BAR_MAX_H}
              stroke="rgba(255,255,255,0.1)"
            />
            <line
              x1={Y_AXIS_W}
              y1={TOP_PAD + BAR_MAX_H}
              x2={width}
              y2={TOP_PAD + BAR_MAX_H}
              stroke="rgba(255,255,255,0.1)"
            />
            {[0, 50, 100].map((tick) => {
              const y = TOP_PAD + BAR_MAX_H - (tick / 100) * BAR_MAX_H;
              return (
                <g key={tick}>
                  <line
                    x1={Y_AXIS_W - 3}
                    y1={y}
                    x2={Y_AXIS_W}
                    y2={y}
                    stroke="rgba(255,255,255,0.2)"
                  />
                  <text
                    x={Y_AXIS_W - 5}
                    y={y + 3}
                    textAnchor="end"
                    fontSize="9"
                    fontFamily="'JetBrains Mono', monospace"
                    fill="#525870"
                  >
                    {tick}
                  </text>
                </g>
              );
            })}

            {buckets.map((bucket, i) => {
              const x = Y_AXIS_W + i * (BAR_W + BAR_GAP) + BAR_GAP / 2;
              const barH = bucket.jobCount === 0 ? 0 : (bucket.meanScorePct / 100) * BAR_MAX_H;
              const y = TOP_PAD + BAR_MAX_H - barH;
              const fill = barColor(bucket.meanScorePct, bucket.jobCount);
              return (
                <g key={bucket.monthKey}>
                  {bucket.jobCount > 0 && (
                    <>
                      <rect x={x} y={y} width={BAR_W} height={barH} fill={fill} rx={2} />
                      <text
                        x={x + BAR_W / 2}
                        y={y - 4}
                        textAnchor="middle"
                        fontSize="9"
                        fontFamily="'JetBrains Mono', monospace"
                        fill="#F0F2FF"
                      >
                        {bucket.meanScorePct.toFixed(0)}
                      </text>
                    </>
                  )}
                  <text
                    x={x + BAR_W / 2}
                    y={TOP_PAD + BAR_MAX_H + 14}
                    textAnchor="middle"
                    fontSize="9"
                    fontFamily="'JetBrains Mono', monospace"
                    fill="#B8BDD6"
                  >
                    {bucket.label}
                  </text>
                  <text
                    x={x + BAR_W / 2}
                    y={TOP_PAD + BAR_MAX_H + 24}
                    textAnchor="middle"
                    fontSize="8"
                    fontFamily="'JetBrains Mono', monospace"
                    fill="#525870"
                  >
                    {bucket.jobCount === 0 ? '·' : `n=${bucket.jobCount}`}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}

function barColor(pct: number, count: number): string {
  if (count === 0) return '#222538';
  if (pct >= 90) return '#00E87A';
  if (pct >= 70) return '#C49BFF';
  if (pct >= 50) return '#FFD56B';
  return '#FF6B7A';
}

const cardStyle: React.CSSProperties = {
  padding: 18,
  background: '#0D0F1C',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
};

const labelStyle: React.CSSProperties = {
  color: '#525870',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
};
